<template>
    <div class="job">
        <div v-if="!has_started">
            <deal-preview :deal="deal" :jobs="jobs" />
        </div>

        <div v-if="has_started">
            <job-objective :campaign="deal" />

            <job-assets :campaign="deal" />

            <deal-proposal-list :deal="deal" />

            <div v-if="deal.signature_request_id || contract_asset" class="box">
                <div class="deal-form-title">
                    Agreement & Contract
                </div>

                Please review the contract agreement below:

                <div v-if="deal.signature_request_id" class="mt-15">
                    <signature-request :campaign="deal" />
                </div>

                <div v-else-if="contract_extension" class="mt-15">
                    <iframe
                        v-if="contract_extension === 'doc'"
                        class="contract-embed"
                        :src="`https://view.officeapps.live.com/op/embed.aspx?src=${contract_asset.url}`"
                        frameborder="0">
                    </iframe>
                    <div v-else class="contract-embed mt-15"></div>
                </div>
            </div>
            <div v-if="deal.content_rights" class="box">
                <div class="deal-form-title">
                    Content Rights
                </div>
                <div class="mt-15">
                    <content-rights-preview :content-rights="deal.content_rights.content_rights" />
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
    .contract-embed {
        background-color: #eee;
        padding: 10px;
        width: 100%;
        height: 600px;
    }
</style>

<script>
    import JobStatus from '../job/partials/JobStatus.vue';
    import JobObjective from '../job/partials/JobObjective.vue';
    import JobTracking from '../job/partials/JobTracking.vue';
    import JobAssets from '../job/partials/JobAssets.vue';
    import DealProposalList from './partials/DealProposalList.vue';
    import SignatureRequest from '../edocument/SignatureRequest';
    import DealPreview from './partials/DealPreview';
    import AffiliateCompensation from '../campaign/proposal/compensation/AffiliateCompensation';
    import ContentRightsPreview from '../campaign/proposal/ContentRightsPreview';
    import PDFObject from '../../../../vendor/pdfobject';

    export default {
        /**
         * The component name
         *
         * @type {String}
         */
        name: 'Deal',

        /**
         * The child components
         *
         * @type {Object}
         */
        components: {
            AffiliateCompensation,
            SignatureRequest,
            DealPreview,
            JobStatus,
            JobObjective,
            JobTracking,
            JobAssets,
            DealProposalList,
            ContentRightsPreview,
        },

        /**
         * The computed properties
         *
         * @type {Object}
         */
        computed: {
            /**
             * The deal
             *
             * @returns {Object}
             */
            deal() {
                return this.$root.getDeal(this.$route.params.id);
            },

            /**
             * Whether this is a promotion
             *
             * @returns {boolean}
             */
            is_promotion() {
                return this.deal && this.deal.partnership_type === 'promotion';
            },

            /**
             * Whether or not the deal has been signed
             *
             * @returns {boolean}
             */
            is_signed() {
                return this.deal && this.deal.signed_at;
            },

            /**
             * Whether or not the deal has started
             *
             * @returns {boolean}
             */
            has_started() {
                return this.deal && this.deal.has_started;
            },

            /**
             * Whether or not the deal has started
             *
             * @returns {boolean}
             */
            has_ended() {
                return this.deal && this.deal.has_ended;
            },

            /**
             * Deal's jobs
             *
             * @returns {Array}
             */
            jobs() {
                return this.$root.getDealJobs(this.deal.id);
            },

            /**
             * The jobs that have not been delivered
             *
             * @returns {Array}
             */
            outstandingJobs() {
                return this.jobs.filter(job => !job.delivered_at);
            },

            /**
             * The contract asset
             *
             * @return {object}
             */
            contract_asset() {
                const contracts = this.deal.assets.filter(asset => asset.type === 'contract');

                return contracts.length ? contracts[0] : null;
            },

            /**
             * The contract extension
             *
             * @returns {string|null}
             */
            contract_extension() {
                if (!this.contract_asset) {
                    return null;
                }

                if (this.contract_asset.mime) {
                    if (this.contract_asset.mime.indexOf('pdf') >= 0) {
                        return 'pdf';
                    }

                    return 'doc';
                }

                const parts = this.contract_asset.url.split('.');
                return parts.length > 1 ? parts[1] : null;
            },
        },

        methods: {
            /**
             * Load the embedded contract
             */
            loadContract() {
                if (!this.contract_asset || this.deal.signature_request_id || this.contract_extension !== 'pdf') {
                    return;
                }

                const options = {
                    pdfOpenParams: { scrollbar: '0', toolbar: '0', statusbar: '0', messages: '0', navpanes: '0' },
                    fallbackLink:  '<p>This browser does not support inline PDFs. Please download the ' +
                        'contract to view it: <a href="[url]">Download Contract</a></p>',
                };

                PDFObject.embed(this.contract_asset.url, '.contract-embed', options);
            },
        },

        /**
         * After the component is created
         */
        created() {
            if (!this.deal) {
                this.$root.go('sponsorships');
            }
        },

        /**
         * When the component is first created
         */
        mounted() {
            if (!this.is_promotion) {
                this.loadContract();
            }
        },
    };
</script>
